/**
 * CompactDdlCard — the compact-popover DDL summary card (§2.9 S13). Rendered
 * INSTEAD OF the DML edit sections when the cursor statement is DDL: a kind
 * chip (mechanical label, e.g. "CREATE TABLE" / "DROP TRIGGER"), the target
 * object name (omitted entirely when unresolved — no placeholder), the
 * model's parsed facts (one line each; omitted section when empty — no fake
 * facts), the honest "structural editing isn't here" capability line, the
 * Design-table hand-off (create/alter-table models only — decision 3), and
 * the Edit-as-SQL escape hatch.
 *
 * No structural DDL editing lives here — that is `CompactDdlEditor`'s job
 * (`visual-ddl-editor/compact/`), which renders this card as its FALLBACK for
 * statements it cannot embed an editable body for. The card itself is
 * read-only chrome over `DdlCardModel`, built from a pure local mini-parse of
 * the statement text (`buildDdlCardModel`) — it never needs a live
 * `VqeEditContext`.
 *
 * The action row (Design table + Close) is the SHARED
 * `CompactDdlFooterActions` so its `vqe-ddl-design-table` /
 * `vqe-ddl-edit-as-sql` testids exist exactly once across the card and the
 * editable arms. The actual hand-off (`openDesignTableFromDdlCard`) and its
 * analytics live one level up, in `CompactVqePopover`.
 *
 * spec: docs/superpowers/specs/2026-06-14-vqe-ux-refinement-design.md § WS3;
 * Plan 9 §2.9/S13. Redesign:
 * .superpowers/sdd/2026-07-28-compact-visual-ddl-editor/ (T3).
 */

import { CompactDdlFooterActions } from '../../../visual-ddl-editor/compact/CompactDdlFooterActions';
import type { DdlCardModel } from '../compactDdlCard';
import { vqeCopy } from '../../ux/vqeCopy';

export interface CompactDdlCardProps {
  readonly model: DdlCardModel;
  /** Close the popover + focus the Monaco editor on the same statement. */
  readonly onEditAsSql: () => void;
  /** Open the Table Designer for `model.designTable`. Unused when it is `null`. */
  readonly onDesignTable?: () => void;
  /** Gates the Design-table button — `null`/`undefined` disables it with a reason. */
  readonly connectionId?: string | null;
  /**
   * Suppress the card's own action row. Set by hosts that already pin the
   * SHARED `CompactDdlFooterActions` outside the scroll body (the compact
   * popover's `CompactDdlShell`) — a second copy would double the
   * `vqe-ddl-design-table` / `vqe-ddl-edit-as-sql` testids.
   */
  readonly hideActions?: boolean;
}

export function CompactDdlCard({
  model,
  onEditAsSql,
  onDesignTable,
  connectionId,
  hideActions = false,
}: CompactDdlCardProps) {
  return (
    <div className="flex flex-col gap-3" data-testid="vqe-compact-ddl-card">
      <div className="flex flex-wrap items-baseline gap-2">
        <span
          className="rounded bg-muted px-1.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-foreground"
          data-testid="vqe-compact-ddl-kind"
        >
          {model.kindLabel}
        </span>
        {model.objectName ? (
          <span
            className="truncate font-mono text-xs text-muted-foreground"
            data-testid="vqe-compact-ddl-object"
          >
            {model.objectName}
          </span>
        ) : null}
      </div>

      {model.facts.length > 0 ? (
        <ul className="flex flex-col gap-1 text-xs text-muted-foreground">
          {model.facts.map((fact, index) => (
            <li key={index} data-testid="vqe-compact-ddl-fact">
              {fact}
            </li>
          ))}
        </ul>
      ) : null}

      <p className="text-xs text-muted-foreground" data-testid="vqe-compact-ddl-capability">
        {vqeCopy.ddlCard.capability}
      </p>

      {hideActions ? null : (
        <CompactDdlFooterActions
          designTable={model.designTable}
          connectionId={connectionId}
          onDesignTable={onDesignTable}
          onEditAsSql={onEditAsSql}
        />
      )}
    </div>
  );
}
